"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bindShortcuts = void 0;
const behavior_1 = require("../behavior");
const blockOps_1 = require("./blockOps");
const indentation_1 = require("./indentation");
const voidInsert_1 = require("./voidInsert");
/**
 * Each handler is responsible for calling `event.preventDefault()` only when
 * it actually consumes the key, so unhandled cases (e.g. `Backspace` outside
 * an empty block) fall through to Slate's default behavior.
 */
const bindShortcuts = (state) => {
    const editor = state.editor;
    const consume = (key, fn) => {
        key.event?.preventDefault();
        fn?.();
        state.sync(true);
    };
    const bindings = [
        // -------------------------------------------------------------- Omni menu
        [
            'Shift Shift',
            () => {
                state.omni.openAtCaret();
            },
        ],
        [
            'P+j',
            (key) => {
                key.event?.preventDefault();
                state.omni.openAsPalette();
            },
        ],
        // Space is a beginner-friendly shortcut: opens in an empty block (caret)
        // or with a range selection. In a non-empty block at the caret, Space
        // falls through to insert a space normally, if menu not opened.
        [
            'Space',
            (key) => {
                if (state.omni.handleSpaceKey())
                    key.event?.preventDefault();
            },
        ],
        // ---------------- Escape a leading void block (`ArrowUp` at document top)
        [
            'ArrowUp',
            (key) => {
                if ((0, voidInsert_1.insertPAboveLeadingVoid)(editor))
                    consume(key);
            },
        ],
        // ----------------------------------------------- Empty-block to paragraph
        [
            'Backspace',
            (key) => {
                if ((0, behavior_1.resetEmptyBlockToP)(editor) || (0, behavior_1.removeEmptyPrevP)(editor) || (0, behavior_1.resetBlockToPAtStart)(editor))
                    consume(key);
            },
        ],
        [
            'Delete',
            (key) => {
                if ((0, behavior_1.resetEmptyBlockToP)(editor))
                    consume(key);
            },
        ],
        // ---------------------------------------- Code-block break / exit (Enter)
        [
            'P+Enter',
            (key) => {
                if ((0, behavior_1.insertCodeBlockBreak)(editor))
                    consume(key);
            },
        ],
        [
            'Shift+Enter',
            (key) => {
                if ((0, behavior_1.insertCodeBlockExit)(editor) || (0, behavior_1.exitStepperList)(editor))
                    consume(key);
            },
        ],
        // ------------------------------------------------------------------ Marks
        ['P+b', (key) => consume(key, () => (0, behavior_1.toggleMark)(editor, 'bold'))],
        ['P+i', (key) => consume(key, () => (0, behavior_1.toggleMark)(editor, 'italic'))],
        ['P+u', (key) => consume(key, () => (0, behavior_1.toggleMark)(editor, 'underline'))],
        ['P+e', (key) => consume(key, () => (0, behavior_1.toggleMark)(editor, 'code'))],
        ['P+Shift+x', (key) => consume(key, () => (0, behavior_1.toggleMark)(editor, 'strikethrough'))],
        // ------------------------------------------------------------------- Link
        [
            'P+k',
            (key) => {
                const link = state.inline.link;
                if (!link.canOpen.value)
                    return;
                key.event?.preventDefault();
                link.setAnchorFromSelection();
                state.inline.dismissed.next(true);
                link.toggle();
            },
        ],
        // ------------------------------------------------------------ Undo / redo
        ['P+z', (key) => consume(key, () => (0, behavior_1.undo)(editor))],
        ['P+Shift+z', (key) => consume(key, () => (0, behavior_1.redo)(editor))],
        ['P+y', (key) => consume(key, () => (0, behavior_1.redo)(editor))],
        // ---------------------------------------------------------- Block toggles
        ['P+Alt+@Digit0', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'p'))],
        ['P+Alt+@Digit1', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h1'))],
        ['P+Alt+@Digit2', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h2'))],
        ['P+Alt+@Digit3', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h3'))],
        ['P+Alt+@Digit4', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h4'))],
        ['P+Alt+@Digit5', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h5'))],
        ['P+Alt+@Digit6', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'h6'))],
        ['P+Alt+@Digit7', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'ol'))],
        ['P+Alt+@Digit8', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'ul'))],
        ['P+Alt+@Digit9', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'blockquote'))],
        ['P+Shift+q', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'blockquote'))],
        ['P+Shift+c', (key) => consume(key, () => (0, behavior_1.toggleBlock)(editor, 'code-block'))],
        // -------------------------------------------------------------- Alignment
        ['P+Shift+l', (key) => consume(key, () => (0, behavior_1.setAlignment)(editor, 'left'))],
        ['P+Shift+e', (key) => consume(key, () => (0, behavior_1.setAlignment)(editor, 'center'))],
        ['P+Shift+r', (key) => consume(key, () => (0, behavior_1.setAlignment)(editor, 'right'))],
        ['P+Shift+j', (key) => consume(key, () => (0, behavior_1.setAlignment)(editor, 'justify'))],
        // ------------------------------------------------------------ Indentation
        ['P+]', (key) => consume(key, () => (0, indentation_1.indentBlock)(editor))],
        ['P+[', (key) => consume(key, () => (0, indentation_1.dedentBlock)(editor))],
        // -------------------------------------------------------- Help / shortcut
        [
            'P+/',
            (key) => {
                key.event?.preventDefault();
                state.shortcutsOpen.set(true);
            },
        ],
        // --------------------------------------------------- Transliteration mode
        // Browser hijacks `Cmd+Shift+T` (reopen last closed tab) before our
        // handler runs, so we use Alt-based bindings instead.
        [
            'P+Alt+@KeyT',
            (key) => {
                key.event?.preventDefault();
                state.translit.toggle();
            },
        ],
        [
            'P+Alt+Shift+@KeyT',
            (key) => {
                key.event?.preventDefault();
                if (state.translit.mapOpen.value)
                    state.translit.closeMap();
                else
                    state.translit.openMap();
            },
        ],
        // ---------------------------------------------------- Display mode toggle
        [
            'P+Shift+m',
            (key) => {
                key.event?.preventDefault();
                state.setDisplayMode(state.displayMode.value === 'inline' ? 'fullwindow' : 'inline');
            },
        ],
        [
            'P+Shift+f',
            (key) => {
                key.event?.preventDefault();
                state.setDisplayMode(state.displayMode.value === 'fullscreen' ? 'inline' : 'fullscreen');
            },
        ],
        // ------------------------------------------------------- Block operations
        [
            'Alt+Shift+ArrowUp',
            (key) => {
                if ((0, blockOps_1.moveBlockUp)(editor))
                    consume(key);
            },
        ],
        [
            'Alt+Shift+ArrowDown',
            (key) => {
                if ((0, blockOps_1.moveBlockDown)(editor))
                    consume(key);
            },
        ],
        [
            'P+Shift+d',
            (key) => {
                if ((0, blockOps_1.duplicateBlock)(editor))
                    consume(key);
            },
        ],
        [
            'P+Shift+k',
            (key) => {
                if ((0, blockOps_1.deleteBlock)(editor))
                    consume(key);
            },
        ],
    ];
    return state.kbd.bind(bindings);
};
exports.bindShortcuts = bindShortcuts;
